// AddActionModal.tsx (updated)
import React, { useState } from "react";
import Dropdown from "../../../../common/Dropdown/Dropdown";
import FormatterBar from "../../../../common/FormatterBar/FormaterBarWithLinkmodel/FormatterBar";
import styles from "./AddActionModal.module.scss";
import { useTranslation } from "react-i18next";
import Button from "../../../../common/Button/Button";
import Icon from "../../../../common/Icon/Icon";
import { useAppSelector } from "../../../../../redux/hooks";
import ErrorMessage from "../../../../../utils/fieldErrorMesssage";
import DatePicker from "../../../../common/DatePicker/DatePicker";
import { formatDateShort, useProfileParams } from "../../../../../utils/common";
import { useCreateGoalActionMutation, useUpdateGoalActionMutation } from "../../../../../redux/services/goalActionsAPI";
import { isEmpty } from "lodash";

type actionDropdownItem = {
  code: string | number;
  value: string
};

const ASSIGNEE: actionDropdownItem[] = [
  { code: 1, value: "hatv_table_header.Account_Manager" },
  { code: 2, value: "leader" },
  { code: 9, value: "unassigned" },
];

interface actionProps {
  onClose: () => void;
  onSave: (actionData: any) => void;
  editData?: ActionDataResp;
  selectedGoalId: number | null;
}
interface ActionErrors {
  actionTitle?: string;
  assignee?: string;
  [key: string]: string | undefined;
}

const AddActionModal: React.FC<actionProps> = ({
  onClose,
  onSave,
  editData,
  selectedGoalId,
}) => {
  const { t } = useTranslation();
  const [textData, setTextData] = useState<string>(editData?.note || "");
  const translatedAssignees = ASSIGNEE.map(item => {
    return {
      ...item,
      value: t(item.value)
    };
  });
  const [assignee, setAssignee] = useState<actionDropdownItem | undefined>(
    translatedAssignees.find((a) => a.code === editData?.assignToType)
  );
  const [actionTitle, setActionTitle] = useState<string>(editData?.title || "");
  const [isActionTitleFocused, setIsActionTitleFocused] = useState(false);
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const [errors, setErrors] = useState<ActionErrors>({});

  const [selectedTargetDate, setSelectedTargetDate] = useState<
    Date | undefined
  >(editData?.targetDate ? new Date(editData?.targetDate) : undefined);

  const [
    createGoalAction,
    { isLoading: isLoadingCreateGoalAction, isError: isCreateGoalActionError },
  ] = useCreateGoalActionMutation();
  const [
    updateGoalAction,
    { isLoading: isLoadingUpdateGoalAction, isError: isUpdateGoalActionError },
  ] = useUpdateGoalActionMutation();
    const { affiliateCode, aboNumber } = useProfileParams();
  

  const handleClickCancel = () => {
    onClose();
  };
  const textAreaChangeHandler = (value: string) => {
    setTextData(value === "<p><br></p>" ? "" : value);
  };
  const validate = (): boolean => {
    const newErrors: ActionErrors = {};

    if (!actionTitle.trim()) {
      newErrors.actionTitle = t("action_title_required");
    }

    if (!assignee?.code) {
      newErrors.assignee = t("assignee_required");
    }

    setErrors(
      Object.fromEntries(
        Object.entries(newErrors).filter(([_, v]) => v !== undefined)
      ) as ActionErrors
    );

    return Object.keys(newErrors).length === 0;
  };

  const handleAddAction = () => {
    if (!validate()) return;
    const actionPayload = {
      aboGoalId: selectedGoalId || "",
      aboGoalActionId: editData?.aboGoalActionId || "",
      title: actionTitle,
      assignToType: assignee?.code || "",
      targetDate: selectedTargetDate
        ? formatDateShort(selectedTargetDate.toString())
        : "",
      note: textData,
    };

    const createUpdateActionAPI = editData?.aboGoalActionId ? updateGoalAction :  createGoalAction;
    createUpdateActionAPI({
      affiliateCode,
      aboNumber,
      payload: actionPayload,
    }).then((resp: any) => {
      const goalActionRespData = resp?.data?.body;
      if (!isEmpty(goalActionRespData)) {
        onSave(goalActionRespData);
      }
    });
    onClose();
  };

  const handleAssigneeSelect = (item: actionDropdownItem) => {
    setAssignee(item);
    if (errors?.assignee) {
      setErrors((prev) => ({ ...prev, assignee: "" }));
    }
  };
  let calendarBoxStyle = {
    color: currentTheme === "dark" ? "#AEAEB2" : "#707070",
    border: currentTheme === "dark" ? "1px solid #636366" : "1px solid #E4E4E4",
    background: currentTheme === "dark" ? "#1C1C1E" : "#fff",
  };

  return (
    <div
      className={`${styles.add_action_modal_cotainer} add_goal_modal_cotainer`}
    >
      <div className={styles.modal_wrapper}>
        <h2 className={styles.modal_title}>
          {editData ? t("edit_action") : t("add_an_action")}
        </h2>
        <div className={`dropdown ${styles.dropdown}`}>
          <div className={styles.input_wrapper}>
            <input
              type="text"
              className={`dropdown_input ${styles.dropdown_input} ${
                actionTitle || isActionTitleFocused
                  ? styles.input_with_text
                  : ""
              } ${errors?.actionTitle ? "error_outline" : ""}`}
              value={actionTitle}
              onChange={(e) => {
                setActionTitle(e.target.value);
                if (errors?.actionTitle)
                  setErrors((prev) => ({ ...prev, actionTitle: "" }));
              }}
              onFocus={() => setIsActionTitleFocused(true)}
              onBlur={() => setIsActionTitleFocused(false)}
            />
            <label
              className={`placeholder ${styles.placeholder} ${
                actionTitle || isActionTitleFocused ? styles.label_float : ""
              }`}
            >
              {`${t("action_title")} *`}
            </label>
          </div>
          <ErrorMessage
            message={errors?.actionTitle}
            className={styles.error_message}
          />
        </div>

        <div className={styles.assignee_dropdown}>
          <Dropdown
            dropdownName={`${t("assigned_to")}*`}
            items={translatedAssignees}
            selectedItem={assignee}
            handleSelectItemCallback={handleAssigneeSelect}
            addScroll
            className={styles.form_dropdown}
            errorMsg={errors?.assignee}
          />
        </div>

        <div className={styles.flex_wrapper}>
          <DatePicker
            selectedDate={selectedTargetDate}
            setSelectedDate={setSelectedTargetDate}
            size="md"
            placeholder={t("target_Date")}
            invalid={!!errors.date}
            errorMsg={errors.date}
            disablePastDate
          />
          {/* </div> */}
        </div>

        <div className={styles.formatter_wrapper}>
          <FormatterBar
            id="addactiondisc"
            testId="addactiondisc"
            editorData={textData}
            handleDataChange={textAreaChangeHandler}
            section="growthplan"
            placeholder={t("add_description")}
            isContentEditable={true}
            maxCharacter={20000}
          />
        </div>
        <div className={`btn_wrapper ${styles.btn_wrapper}`}>
          <Button
            id="cancelAction"
            onClick={handleClickCancel}
            label={t("AP_info.btnCancel")}
            style="unfilled"
          />
          <Button
            id="saveAction"
            onClick={handleAddAction}
            label={t("AP_info.btnSave")}
            style="filled"
          />
        </div>
      </div>
    </div>
  );
};

export default AddActionModal;
